import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, OnDestroy, OnInit, OnChanges, SimpleChanges, NgZone, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { AiService } from '../../../services/ai.service';
import { AuthService } from 'src/app/_services/auth.service';
import { AI_ASSISTANT_NAME } from 'src/app/_core/config/app-config.const';
import { CreateTangramService } from '../crearte-tangram.service';
import { environment } from 'src/environments/environment';

// Hard upper bound on the finalize round-trip. If the backend truly hasn't
// responded in this window the network/proxy is almost certainly the cause,
// and hanging on an in-flight XHR indefinitely just strands the user on the
// finalizing animation. 75s is comfortably above the slowest observed
// successful call (~22s) so a healthy backend won't trip it.
const FINALIZE_PLAN_TIMEOUT_MS = 75000;

// State machine for the upload flow:
//   idle          — file selected, awaiting provider choice + optional user comment
//   uploading     — AI extraction in progress
//   clarification — AI asked us to answer a few questions before planning
//   finalizing    — answers submitted; AI is building the plan (multi-stage animation)
//   extracted     — review/edit AI-extracted sections
//   decision      — user picks single-Tangram or multi-Tangram creation mode
//   mediaSetup    — per-item configuration (preview / YouTube trim / validation)
//   grouping      — (multi-mode only) group items into separate Tangrams
//   creating      — single or batch create in progress
//   done          — success (single) or per-group summary (multi)
type DocAiState =
  | 'idle'
  | 'uploading'
  | 'clarification'
  | 'finalizing'
  | 'extracted'
  | 'decision'
  | 'mediaSetup'
  | 'grouping'
  | 'creating'
  | 'done';

/**
 * A single stage in the "finalizing" animation. The backend call usually
 * takes 10-20 seconds, so we walk the user through a sequence of stages to
 * show progress rather than a single indefinite spinner.
 */
interface FinalizeStage {
  label: string;
  status: 'pending' | 'active' | 'done';
}

interface ClarificationQuestion {
  id: string;
  question: string;
  suggestedOptions: string[];
  allowCustom: boolean;
  // 'text'  → plain answer (option chips + free-text box)
  // 'links' → source-collection question for link-less documents; the user
  //           pastes URLs into repeatable rows (see clarificationLinks).
  inputType: 'text' | 'links';
}

// One row in the "attach source links" input for a link-collection question.
// `label` is optional (e.g. the reading/session title); `url` is the source.
interface ClarificationLinkRow {
  label: string;
  url: string;
}

interface AiSuggestedTangram {
  tangramIndex: number;
  tangramTitle: string;
  tangramDescription: string;
  sectionIds: string[];
}

/**
 * View-model for the extracted-state UI: one card per Tangram, each with its
 * media sections. Sections are references into the flat `this.sections`
 * array, so ngModel edits propagate to both the grouped view and the flat
 * list used later for media setup.
 */
interface PlanGroup {
  tangramIndex: number;
  tangramTitle: string;
  tangramDescription: string;
  overallSummary: string;
  mediaCount: number;
  sections: any[];
  thumbnailUrl: string;
  extractedLinks: string[];
}

type CreationMode = 'single' | 'multi' | null;

type PlatForm = 'YouTube' | 'Spotify' | 'Other' | 'None';

interface TimeObj { hour: number; minute: number; second: number; }

interface MediaSetupItem {
  // Stable id — groups reference items by id so reordering / moving between
  // groups doesn't depend on array index.
  id: string;
  sectionIndex: number;
  // The AI's proposed Tangram assignment for this section (index into
  // `aiSuggestedTangrams`). -1 when the AI produced a single-tangram plan or
  // the section didn't carry an assignment.
  aiTangramIndex: number;
  title: string;
  description: string;
  videoURL: string;
  type: string;
  order: number;
  platForm: PlatForm;
  youtubeVideoId: string;
  iFrameURL: SafeResourceUrl | null;
  previewURL: SafeResourceUrl | null;
  // Click-to-play gate — the template only mounts the YouTube iframe once
  // the user presses the play overlay, so an 8-item plan doesn't burst a
  // few hundred log/ad/lottie requests on open.
  previewPlayerActive: boolean;
  thumbnail: { smallURL: string; mediumURL: string };
  duration: TimeObj;
  durationSec: number;
  startTime: TimeObj;
  endTime: TimeObj;
  startTimeInSecond: number;
  endTimeInSecond: number;
  tagId: string;
  isResolving: boolean;
  isResolved: boolean;
  isYoutubeIdValid: boolean;
  resolveError: string;
  startTimeError: string;
  endTimeError: string;
  placeholderPath: string;
}

interface TangramGroup {
  id: string;
  title: string;
  description: string;
  itemIds: string[];
}

interface CreationResult {
  groupId: string;
  groupTitle: string;
  status: 'pending' | 'creating' | 'success' | 'failed';
  pathwayId?: string;
  mediaCount?: number;
  error?: string;
}

const ALLOWED_MIME = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];
const ALLOWED_EXT = /\.(pdf|docx|txt)$/i;
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const ZERO_TIME: TimeObj = { hour: 0, minute: 0, second: 0 };

@Component({
  selector: 'tangram-create-from-upload',
  templateUrl: './create-from-upload.component.html',
  styleUrls: ['./create-from-upload.component.scss'],
})
export class CreateFromUploadComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInput: ElementRef<HTMLInputElement>;
  @Output() draftCreated = new EventEmitter<{ pathwayId: string; tangramTitle: string; mediaCount: number }>();

  /**
   * Entry-point hand-off for callers that already ran extraction themselves
   * (the pasted-text flow). When set, the component skips its own idle view
   * and immediately drives the state machine from the provided response —
   * transitioning to `clarification` or `extracted` depending on
   * `response.stage`. Leaving this as `null` preserves the original
   * file-upload behaviour byte-for-byte.
   */
  @Input() preExtractedResponse: any = null;
  @Input() preExtractedProvider: 'gemini' | 'claude' | null = null;
  /** File dropped or browsed in the universal input on the parent page. When set,
   *  the idle view pre-populates the file-ready state so the user can go straight
   *  to "Generate Tangram using Gemini / Claude" without dropping again. */
  @Input() preloadedFile: File | null = null;

  /**
   * Emitted when the user starts over from a pre-extracted hand-off — the
   * host component (create-from-text) uses this to re-show its own idle
   * view instead of letting the file dropzone flash on screen.
   */
  @Output() resetRequested = new EventEmitter<void>();

  state: DocAiState = 'idle';
  uploadedFile: File | null = null;
  isDragging = false;
  errorMessage = '';

  // True when the most recent extraction finished but produced no usable
  // sections — either because the backend explicitly rejected the document
  // ("No extractable links or media…") or because the analyser returned
  // `sections: []`. Drives the friendly empty-state card in the idle view.
  extractionEmpty = false;

  // True when the component was mounted with `preExtractedResponse`. Controls
  // a handful of user-visible differences (no idle dropzone if the user
  // clears, the "Clear" button emits resetRequested instead of flashing the
  // file dropzone, etc.).
  private bootedFromPreExtracted = false;

  // Optional free-form guidance the user can provide before kicking off the
  // AI analysis. When non-empty, the backend skips the clarification branch
  // and commits to a plan directly — see `extractWithAI()`.
  userComment = '';

  extraction: any = null;
  sections: any[] = [];
  createdPathwayId = '';

  // Which AI provider produced the current extraction (for the "Compare AI"
  // flow — users can run the same document through both and switch views).
  // Every user-facing label goes through this — the UI never names the
  // underlying model. See AI_ASSISTANT_NAME in app-config.const.ts.
  readonly aiName = AI_ASSISTANT_NAME;

  activeProvider: 'gemini' | 'claude' | null = null;
  // Loading flag per-provider so only the clicked button shows its spinner.
  providerLoading: 'gemini' | 'claude' | null = null;

  // ── Clarification step state ────────────────────────────────────
  // When the AI returns stage="clarification_required" on first upload we
  // stash the question list + the already-extracted document text here so
  // the follow-up finalizePlan call doesn't need a second upload.
  clarificationQuestions: ClarificationQuestion[] = [];
  clarificationAnswers: { [id: string]: string } = {};
  // For link-collection questions only: repeatable {label,url} rows keyed by
  // question id. Serialized into clarificationAnswers[id] on submit.
  clarificationLinks: { [id: string]: ClarificationLinkRow[] } = {};
  // Behaviour-preference chip selected for a link question, tracked apart from
  // the pasted URLs so choosing one doesn't clobber the link rows.
  clarificationLinkPref: { [id: string]: string } = {};
  // True when the document had structure but no URLs — the AI is asking the
  // user to supply source links before any media items can be built.
  needsSourceLinks = false;
  clarificationReasoning = '';
  clarificationLoading = false;
  clarificationError = '';
  private pendingDocumentText = '';
  private pendingDocumentStructureId = '';
  private pendingFilename = '';

  // ── Finalizing animation state ──────────────────────────────────
  // Used only when `state === 'finalizing'`. The stages advance on a timer
  // so the user sees progression rather than an indefinite spinner while
  // the AI builds the plan (typically 10-20s).
  finalizeStages: FinalizeStage[] = [];
  // Flipped after ~30s so the view can surface a "still working" message
  // with a manual cancel/retry option. Gives the user an escape hatch if
  // a proxy ate the response and we're actually stuck.
  finalizeSlow = false;
  private finalizeTimer: any = null;
  private finalizeSlowTimer: any = null;
  private static readonly FINALIZE_STAGE_LABELS = [
    'Understanding your answers',
    'Mapping the Tangrams',
    'Refining titles and descriptions',
    'Organising the media items',
    'Finalising the plan',
  ];
  private static readonly FINALIZE_SLOW_HINT_MS = 30000;

  // ── AI-suggested multi-tangram split ────────────────────────────
  // When the AI returns more than one Tangram, we preserve its proposal so
  // the grouping step can pre-fill with the AI split rather than one-item-
  // per-Tangram. The user can still regroup manually from there.
  aiSuggestedTangrams: AiSuggestedTangram[] = [];

  // ── Media-setup step state ──────────────────────────────────────
  mediaSetupItems: MediaSetupItem[] = [];
  mediaSetupError = '';

  // ── Decision / multi-tangram grouping state ─────────────────────
  // `creationMode` is set when the user picks Option A (single) or Option B
  // (multiple tangrams) on the decision step. Option A preserves the
  // original one-tangram-many-media behaviour; Option B enters the
  // grouping step where the user splits items across multiple tangrams.
  creationMode: CreationMode = null;
  groups: TangramGroup[] = [];
  get groupSelectOptions() {
    return this.groups.map((g, i) => ({
      value: g.id,
      label: 'Tangram ' + (i + 1) + (g.title ? ' — ' + g.title : ''),
    }));
  }
  creationResults: CreationResult[] = [];
  groupingError = '';
  // Set true while a multi-batch is in flight; used by the template to
  // disable the Create / Merge / Reset / Add controls so the user can't
  // double-submit or mutate the group list mid-flight.
  isBatchCreating = false;
  private groupSeq = 0;

  private currentUser: any = null;
  private isUserLoggedIn = false;
  private userSub: Subscription;

  constructor(
    private aiService: AiService,
    private toaster: ToastrService,
    private router: Router,
    private authService: AuthService,
    private createTangramService: CreateTangramService,
    private sanitizer: DomSanitizer,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    this.userSub = this.authService.currentUser.subscribe((u) => {
      this.currentUser = u;
      this.isUserLoggedIn = !!u;
    });
  }

  ngOnInit() {
    // Pre-populate from a file dropped in the parent universal input.
    if (this.preloadedFile) {
      this.uploadedFile = this.preloadedFile;
    }
    // When the component is mounted by another flow (e.g. create-from-text)
    // that already ran extraction, jump straight into the state machine from
    // that response instead of rendering our own idle view.
    if (this.preExtractedResponse) {
      this.bootedFromPreExtracted = true;
      this.applyExtractionResponse(this.preExtractedResponse, this.preExtractedProvider || undefined);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Re-run analysis if the host component swaps in a fresh response after
    // the first extraction (e.g. user re-ran "Generate Tangram using Gemini" in the
    // text flow after switching providers).
    if (changes && changes.preExtractedResponse && !changes.preExtractedResponse.firstChange) {
      const next = changes.preExtractedResponse.currentValue;
      if (next) {
        this.bootedFromPreExtracted = true;
        this.applyExtractionResponse(next, this.preExtractedProvider || undefined);
      }
    }
  }

  ngOnDestroy() {
    if (this.userSub) { this.userSub.unsubscribe(); }
    this.stopFinalizeStepper();
  }

  /**
   * Public entry-point for callers that already ran extraction (the
   * pasted-text flow). Routes to the correct state-machine branch based on
   * `response.stage`. Accepts the raw HTTP-envelope response shape
   * (`{ success, stage, data, provider }`) so create-from-text can pass the
   * extractDocument observable result through verbatim.
   */
  applyExtractionResponse(response: any, provider?: 'gemini' | 'claude'): boolean {
    if (!response || !response.success || !response.data) {
      const msg = (response && response.message) || 'Extraction failed.';
      this.errorMessage = msg;
      return false;
    }
    const data = response.data;
    const stage = response.stage || data.stage || 'plan_ready';
    const resolvedProvider = (response.provider || data.provider || provider || 'claude') as 'gemini' | 'claude';
    this.activeProvider = resolvedProvider;
    this.errorMessage = '';

    if (stage === 'clarification_required') {
      this.enterClarification(data);
      return true;
    }
    const applied = this.applyPlanResponse(data);
    if (!applied) {
      this.errorMessage = 'The AI could not identify any sections in this content.';
    }
    return applied;
  }

  // ── Finalizing animation stepper ───────────────────────────────
  /**
   * Start the progressive stage animation for the `finalizing` state. Each
   * stage promotes pending → active → done on a timer. The last stage stays
   * `active` until the API responds so the user never sees an incomplete
   * final step before the view transitions away.
   */
  private startFinalizeStepper() {
    this.stopFinalizeStepper();
    this.finalizeSlow = false;
    this.finalizeStages = CreateFromUploadComponent.FINALIZE_STAGE_LABELS.map((label, i) => ({
      label,
      status: i === 0 ? 'active' : 'pending',
    }));
    // ~2.3s per stage feels like real progression without finishing the
    // whole list before a typical 10-20s AI call returns. The last stage is
    // latched — see check below.
    this.finalizeTimer = setInterval(() => {
      const activeIdx = this.finalizeStages.findIndex((s) => s.status === 'active');
      if (activeIdx === -1 || activeIdx >= this.finalizeStages.length - 1) {
        return; // hold on the final stage until the API responds
      }
      this.finalizeStages[activeIdx].status = 'done';
      this.finalizeStages[activeIdx + 1].status = 'active';
    }, 2300);
    // Safety net — if the call takes longer than expected the view surfaces
    // a "still working" message + cancel button so the user isn't stranded
    // on the animation when a proxy silently eats the 200.
    this.finalizeSlowTimer = setTimeout(() => {
      this.zone.run(() => {
        this.finalizeSlow = true;
        try { this.cdr.detectChanges(); } catch (_) { /* view may be destroyed */ }
      });
    }, CreateFromUploadComponent.FINALIZE_SLOW_HINT_MS);
  }

  private stopFinalizeStepper() {
    if (this.finalizeTimer) {
      clearInterval(this.finalizeTimer);
      this.finalizeTimer = null;
    }
    if (this.finalizeSlowTimer) {
      clearTimeout(this.finalizeSlowTimer);
      this.finalizeSlowTimer = null;
    }
    this.finalizeSlow = false;
  }

  // ── File selection ──────────────────────────────────────────────
  onDragOver(e: DragEvent) { e.preventDefault(); this.isDragging = true; }
  onDragLeave(e: DragEvent) { e.preventDefault(); this.isDragging = false; }
  onDrop(e: DragEvent) {
    e.preventDefault();
    this.isDragging = false;
    const files = e.dataTransfer ? e.dataTransfer.files : null;
    if (files && files.length > 0) { this.handleFile(files[0]); }
  }
  onBrowse(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
      // Reset so the same file can be picked again after clearing
      input.value = '';
    }
  }
  triggerBrowse() {
    if (this.uploadedFile) { return; }
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  private handleFile(file: File) {
    this.errorMessage = '';
    const typeOk = file.type && ALLOWED_MIME.indexOf(file.type) !== -1;
    const extOk = ALLOWED_EXT.test(file.name);
    if (!typeOk && !extOk) {
      const msg = 'Unsupported file. Please upload a PDF, DOCX, or TXT file.';
      this.errorMessage = msg;
      this.toaster.error(msg);
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      const msg = 'File is larger than 10 MB. Please upload a smaller document.';
      this.errorMessage = msg;
      this.toaster.error(msg);
      return;
    }
    this.uploadedFile = file;
    this.state = 'idle';
    this.extraction = null;
    this.sections = [];
    this.createdPathwayId = '';
    this.mediaSetupItems = [];
    this.mediaSetupError = '';
    this.creationMode = null;
    this.groups = [];
    this.creationResults = [];
    this.groupingError = '';
    this.isBatchCreating = false;
    this.resetClarificationState(true);
    this.aiSuggestedTangrams = [];
    this.planGroups = [];
    this.planMediaTotal = 0;
    this.extractionEmpty = false;
  }

  clearFile() {
    // When this component was mounted with pre-extracted data (text flow)
    // the "idle" view is meaningless — the host wants to re-show its own
    // rich-text editor instead of our file dropzone. Bail out, emit, and
    // let the host decide what to do next.
    if (this.bootedFromPreExtracted) {
      this.resetRequested.emit();
      return;
    }
    this.uploadedFile = null;
    this.state = 'idle';
    this.errorMessage = '';
    this.extraction = null;
    this.sections = [];
    this.createdPathwayId = '';
    this.activeProvider = null;
    this.providerLoading = null;
    this.mediaSetupItems = [];
    this.mediaSetupError = '';
    this.creationMode = null;
    this.groups = [];
    this.creationResults = [];
    this.groupingError = '';
    this.isBatchCreating = false;
    this.userComment = '';
    this.resetClarificationState(true);
    this.aiSuggestedTangrams = [];
    this.planGroups = [];
    this.planMediaTotal = 0;
    this.extractionEmpty = false;
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private resetClarificationState(clearStoredStructure = false) {
    this.clarificationQuestions = [];
    this.clarificationAnswers = {};
    this.clarificationLinks = {};
    this.clarificationLinkPref = {};
    this.needsSourceLinks = false;
    this.clarificationReasoning = '';
    this.clarificationLoading = false;
    this.clarificationError = '';
    this.pendingDocumentText = '';
    if (clearStoredStructure) {
      this.pendingDocumentStructureId = '';
    }
    this.pendingFilename = '';
    this.stopFinalizeStepper();
    this.finalizeStages = [];
  }

  removeSection(index: number) {
    this.sections.splice(index, 1);
    this.rebuildPlanGroups();
  }

  /** Remove a section by reference — used by the grouped plan UI, which
   * iterates over `planGroups[i].sections` (references into `this.sections`). */
  removeSectionByRef(section: any) {
    const idx = this.sections.indexOf(section);
    if (idx !== -1) {
      this.sections.splice(idx, 1);
      this.rebuildPlanGroups();
    }
  }

  // ── Plan view-model ─────────────────────────────────────────────
  // Cached to avoid recomputing on every Angular change-detection tick.
  // `rebuildPlanGroups()` is the only place these get populated — call it
  // after any mutation to `this.extraction` or `this.sections`.
  planGroups: PlanGroup[] = [];
  get planGroupSelectOptions() {
    return this.planGroups.map((g, i) => ({
      value: g.tangramIndex.toString(),
      label: 'Tangram ' + (i + 1),
    }));
  }
  planMediaTotal = 0;

  // Accordion state for the plan review — set of collapsed Tangram-card indices.
  private collapsedGroups = new Set<number>();
  isGroupCollapsed(gi: number): boolean {
    return this.collapsedGroups.has(gi);
  }
  toggleGroupCollapsed(gi: number) {
    if (this.collapsedGroups.has(gi)) {
      this.collapsedGroups.delete(gi);
    } else {
      this.collapsedGroups.add(gi);
    }
  }

  /**
   * Rebuild the grouped plan view-model. Called explicitly after the plan
   * changes (apply response, remove media, etc.) rather than recomputed on
   * every CD cycle — which matters a lot for large plans because the
   * extracted template walks `planGroups` and every section inside it on
   * every tick.
   *
   * Falls back to a single synthetic group for legacy single-tangram
   * responses (where the backend didn't send `extraction.tangrams`).
   */
  rebuildPlanGroups() {
    this.planMediaTotal = this.sections.length;

    if (!this.extraction || !this.sections.length) {
      this.planGroups = [];
      return;
    }

    const tangrams = this.extraction && Array.isArray(this.extraction.tangrams)
      ? this.extraction.tangrams
      : null;

    if (tangrams && tangrams.length) {
      // Normalize tangramIndex to match array position. After reorder
      // operations the stamped `t.tangramIndex` can drift, so keep it in
      // sync with the array slot — the dropdown move writes array positions,
      // so filtering must also be by array position.
      tangrams.forEach((t: any, idx: number) => { t.tangramIndex = idx; });
      this.planGroups = tangrams.map((t: any, idx: number) => {
        const tangramSections = this.sections.filter(
          (s) => (typeof s.tangramIndex === 'number' ? s.tangramIndex : 0) === idx,
        );
        return {
          tangramIndex: idx,
          tangramTitle: (t.tangramTitle || '').trim() || ('Tangram ' + (idx + 1)),
          tangramDescription: (t.tangramDescription || '').trim(),
          overallSummary: (t.overallSummary || '').trim(),
          mediaCount: tangramSections.length,
          sections: tangramSections,
          thumbnailUrl: this.deriveSectionThumbnail(tangramSections[0]),
          extractedLinks: Array.isArray(t.extractedLinks) ? t.extractedLinks : [],
        };
      });
      return;
    }

    // Legacy single-tangram fallback — project top-level extraction fields.
    this.planGroups = [{
      tangramIndex: 0,
      tangramTitle: (this.extraction.tangramTitle || '').trim() || 'Untitled Tangram',
      tangramDescription: (this.extraction.tangramDescription || '').trim(),
      overallSummary: (this.extraction.overallSummary || '').trim(),
      mediaCount: this.sections.length,
      sections: this.sections,
      thumbnailUrl: this.deriveSectionThumbnail(this.sections[0]),
      extractedLinks: Array.isArray(this.extraction.extractedLinks) ? this.extraction.extractedLinks : [],
    }];
  }

  /** trackBy for the plan-tangrams *ngFor — keeps DOM stable across edits. */
  trackPlanGroup = (_: number, g: PlanGroup) => g.tangramIndex;
  /** trackBy for the plan-media *ngFor — identity-based since each section
   * is the same object reference before/after an inline edit. */
  trackSection = (_: number, s: any) => s;

  /**
   * Best-effort thumbnail for a Tangram card: derive from YouTube URLs up
   * front (no extra API call), otherwise empty so the template can render a
   * type-based placeholder icon. Proper Spotify / OG thumbnails are
   * resolved in the media-setup step.
   *
   * We use `mqdefault.jpg` (320×180) instead of `hqdefault.jpg` because
   * mqdefault is always served for any valid YouTube video id, while
   * hqdefault occasionally 404s for shorts and some auto-generated videos
   * even when the video itself is playable.
   */
  private deriveSectionThumbnail(section: any): string {
    if (!section || !section.videoURL) { return ''; }
    const detected = this.detectPlatform(section.videoURL);
    if (detected.platForm === 'YouTube' && detected.youtubeVideoId) {
      return 'https://i.ytimg.com/vi/' + detected.youtubeVideoId + '/mqdefault.jpg';
    }
    return '';
  }

  /**
   * Replace a broken Tangram thumbnail with the SVG placeholder. Called
   * from the template's `(error)` handler — clears the group's
   * `thumbnailUrl` so `*ngIf="group.thumbnailUrl"` swaps the `<img>` for
   * the icon underneath (instead of leaving an empty rectangle).
   */
  onThumbnailError(group: PlanGroup) {
    if (!group) { return; }
    group.thumbnailUrl = '';
    try { this.cdr.detectChanges(); } catch (_) { /* view may be destroyed */ }
  }

  // ── AI extraction ───────────────────────────────────────────────
  /**
   * Run the extraction through the chosen provider. Called with 'gemini' or
   * 'claude' from the two-button UI; the same function handles re-extraction
   * so users can compare side-by-side without re-uploading the file.
   *
   * Three possible outcomes:
   *  1. Backend returns `stage: "clarification_required"` — we stash the
   *     questions + already-extracted text and move to the `clarification`
   *     view. The user answers then triggers `submitClarification()`.
   *  2. Backend returns `stage: "plan_ready"` — we apply the plan (which may
   *     carry multiple tangrams) and move to the `extracted` review view.
   *  3. Error / empty result — we surface the error and stay on idle.
   */
  private handleExtractionResponse(response: any, provider: 'gemini' | 'claude') {
    this.providerLoading = null;
    if (!response || !response.success || !response.data) {
      const msg = (response && response.message) || 'Extraction failed. Please try again.';
      this.errorMessage = msg;
      this.extractionEmpty = CreateFromUploadComponent.isEmptyContentMessage(msg);
      if (!this.extractionEmpty) { this.toaster.error(msg); }
      this.state = 'idle';
      return;
    }

    const data = response.data;
    const stage = response.stage || data.stage || 'plan_ready';
    this.activeProvider = (response.provider || data.provider || provider) as 'gemini' | 'claude';

    if (stage === 'clarification_required') {
      this.enterClarification(data);
      return;
    }

    const applied = this.applyPlanResponse(data);
    if (!applied) {
      this.errorMessage = 'No links or media items could be created from this document. Try another file or add a comment describing what you want.';
      this.extractionEmpty = true;
      this.state = 'idle';
    }
  }

  extractWithAI(provider: 'gemini' | 'claude' = 'claude') {
    if (!this.uploadedFile && !this.pendingDocumentStructureId) { return; }
    if (!this.isUserLoggedIn) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: '/create-tangram' } });
      return;
    }
    // When re-extracting with the other provider we keep the current results
    // on screen (with a loading banner) rather than unmounting them, so the
    // user doesn't lose scroll context while comparing.
    const isReExtract = (this.state === 'extracted' || this.state === 'clarification') && !!this.extraction;
    if (!isReExtract) { this.state = 'uploading'; }
    this.providerLoading = provider;
    this.errorMessage = '';
    this.extractionEmpty = false;

    // When structure was stored on the first run, reuse it (stored text +
    // detected blocks) and only send the new provider / user comment.
    this.aiService.extractDocument(
      this.pendingDocumentStructureId ? null : this.uploadedFile,
      provider,
      this.userComment,
      this.pendingDocumentStructureId || undefined,
    ).subscribe(
      (response: any) => this.handleExtractionResponse(response, provider),
      (err: any) => {
        this.providerLoading = null;
        const msg = (err && err.error && err.error.message) || 'Failed to extract document. Please try again.';
        this.errorMessage = msg;
        this.extractionEmpty = CreateFromUploadComponent.isEmptyContentMessage(msg);
        if (!this.extractionEmpty) { this.toaster.error(msg); }
        this.state = 'idle';
      },
    );
  }

  /**
   * Detect the "no extractable content" class of error — matches both the
   * backend 422 ("No extractable links or media were found…") and the
   * client-side success-but-empty fallback above. Anything else is treated
   * as a transient error and keeps the red toast.
   */
  private static isEmptyContentMessage(msg: string): boolean {
    if (!msg) { return false; }
    const m = msg.toLowerCase();
    return m.includes('no extractable')
      || m.includes('no links or media')
      || m.includes('no usable')
      || m.includes('could not identify any sections');
  }

  // ── Clarification step ──────────────────────────────────────────
  /**
   * Seed the clarification form from the backend response. We also keep the
   * already-extracted document text here so the finalize call can re-use it
   * without a second upload.
   */
  private enterClarification(data: any) {
    const questions: any[] = Array.isArray(data.clarificationQuestions) ? data.clarificationQuestions : [];
    this.needsSourceLinks = !!(data.documentStructure && data.documentStructure.needsSources);
    this.clarificationQuestions = questions.map((q, i) => {
      const id = q.id || ('q_' + (i + 1));
      const question = q.question || '';
      const isLinkQ = CreateFromUploadComponent.isSourceLinkQuestion(id, question);
      return {
        id,
        question,
        suggestedOptions: Array.isArray(q.suggestedOptions) ? q.suggestedOptions : [],
        allowCustom: q.allowCustom !== false,
        inputType: isLinkQ ? ('links' as const) : ('text' as const),
      };
    });
    this.clarificationAnswers = {};
    this.clarificationLinks = {};
    this.clarificationLinkPref = {};
    for (const q of this.clarificationQuestions) {
      this.clarificationAnswers[q.id] = '';
      if (q.inputType === 'links') {
        // Seed a couple of blank rows so the user has somewhere to paste.
        this.clarificationLinks[q.id] = [{ label: '', url: '' }, { label: '', url: '' }];
      }
    }
    this.clarificationReasoning = data.analysis && data.analysis.reasoning ? String(data.analysis.reasoning) : '';
    this.pendingDocumentStructureId = String(data.documentStructureId || '');
    this.pendingDocumentText = String(data.documentText || '');
    this.pendingFilename = String(data.filename || (this.uploadedFile ? this.uploadedFile.name : ''));
    this.clarificationError = '';
    this.state = 'clarification';
  }

  /** Quick-select — the user picked a suggested option. */
  chooseClarificationOption(questionId: string, option: string) {
    const q = this.clarificationQuestions.find((x) => x.id === questionId);
    if (q && q.inputType === 'links') {
      // For a link question the chips are behaviour preferences, not the
      // answer itself — toggle them without touching the pasted URLs.
      this.clarificationLinkPref[questionId] = this.clarificationLinkPref[questionId] === option ? '' : option;
      return;
    }
    this.clarificationAnswers[questionId] = option;
  }

  /** Is this the source-collection question for a link-less document? */
  private static isSourceLinkQuestion(id: string, question: string): boolean {
    if (id === 'q_sources') { return true; }
    const q = (question || '').toLowerCase();
    return q.includes('source url')
      || q.includes('paste source')
      || /\b(paste|attach|add|provide|supply)\b[^.]*\b(url|urls|link|links)\b/.test(q);
  }

  /** Append a blank link row to a link-collection question. */
  addClarificationLink(questionId: string) {
    const rows = this.clarificationLinks[questionId] || (this.clarificationLinks[questionId] = []);
    rows.push({ label: '', url: '' });
  }

  /** Remove a link row, keeping at least one blank row so the UI never empties. */
  removeClarificationLink(questionId: string, index: number) {
    const rows = this.clarificationLinks[questionId];
    if (!rows) { return; }
    rows.splice(index, 1);
    if (rows.length === 0) { rows.push({ label: '', url: '' }); }
  }

  /** trackBy for the link rows so ngModel focus survives add/remove. */
  trackByLinkIndex(index: number): number {
    return index;
  }

  /**
   * Lightweight client-side check: does this look like a pasteable URL? Used
   * only to flag obviously-broken rows inline — the backend still does the
   * authoritative media-source validation.
   */
  isLikelyUrl(url: string): boolean {
    const u = (url || '').trim();
    if (!u) { return true; }
    if (/\s/.test(u)) { return false; }
    return /^https?:\/\/\S+\.\S+/i.test(u) || /^\S+\.\S+/.test(u);
  }

  /**
   * Fold each link question's {label,url} rows (plus any selected behaviour
   * chip) into clarificationAnswers[id], formatted the way the backend parses
   * source links: one "Label — https://…" (or bare URL) per line.
   */
  private serializeClarificationLinks() {
    for (const q of this.clarificationQuestions) {
      if (q.inputType !== 'links') { continue; }
      const lines: string[] = [];
      const pref = (this.clarificationLinkPref[q.id] || '').trim();
      if (pref) { lines.push(pref); }
      for (const row of (this.clarificationLinks[q.id] || [])) {
        const url = (row.url || '').trim();
        if (!url) { continue; }
        const label = (row.label || '').trim();
        lines.push(label ? `${label} — ${url}` : url);
      }
      this.clarificationAnswers[q.id] = lines.join('\n');
    }
  }

  /**
   * Second round-trip: send the answers (plus any refined user comment) to
   * the backend, get back a final plan, and fall through to the `extracted`
   * review view.
   */
  submitClarification() {
    if (!this.pendingDocumentStructureId && !this.pendingDocumentText) {
      // Should never happen — the clarification view isn't shown without
      // stored structure — but guard anyway so we don't 400 the server.
      this.toaster.error('Session expired. Please upload the document again.');
      this.state = 'idle';
      return;
    }
    // Fold pasted URLs into the answer map before validation so link
    // questions are checked on their serialized value.
    this.serializeClarificationLinks();

    // A link-collection question needs at least one real URL, and every URL
    // the user did type must look pasteable.
    // Source links are OPTIONAL: if the user attaches none, the backend builds
    // placeholder media rows they can fill in later (and blocks publishing
    // until they do). We only validate the links they DID type.
    for (const q of this.clarificationQuestions) {
      if (q.inputType !== 'links') { continue; }
      const rows = this.clarificationLinks[q.id] || [];
      if (rows.some((r) => (r.url || '').trim() && !this.isLikelyUrl(r.url))) {
        this.clarificationError = 'One of the links doesn’t look like a valid URL. Please check it or clear it to continue.';
        return;
      }
    }

    // Link-collection questions may be left blank (→ placeholders), so they
    // don't count towards the "answer every question" requirement.
    const unanswered = this.clarificationQuestions.filter(
      (q) => q.inputType !== 'links' && !((this.clarificationAnswers[q.id] || '').trim()),
    );
    if (unanswered.length > 0) {
      this.clarificationError = 'Please answer all questions (or pick a suggested option) before continuing.';
      return;
    }

    this.clarificationError = '';
    this.clarificationLoading = true;
    // Swap to the dedicated finalizing view so the user gets a proper AI
    // "working on it" animation instead of a hidden spinner behind a
    // disabled button while the plan is being built.
    this.state = 'finalizing';
    this.startFinalizeStepper();

    const payload: {
      documentStructureId?: string;
      documentText?: string;
      filename: string;
      userComment: string;
      clarificationAnswers: { [id: string]: string };
      provider: 'gemini' | 'claude';
    } = {
      filename: this.pendingFilename,
      userComment: this.userComment,
      clarificationAnswers: { ...this.clarificationAnswers },
      provider: this.activeProvider || 'claude',
    };
    if (this.pendingDocumentStructureId) {
      payload.documentStructureId = this.pendingDocumentStructureId;
    } else {
      payload.documentText = this.pendingDocumentText;
    }
    const startedAt = Date.now();
    // eslint-disable-next-line no-console
    console.log('[finalizePlan] POST → /ai/finalize-plan', { provider: payload.provider, questions: Object.keys(payload.clarificationAnswers).length });

    // Every branch below runs inside NgZone and through `finishFinalize()` so
    // state transitions can't skip change detection on slow machines — that
    // was the underlying cause of the "white screen after 200" we saw when
    // the response handler fired during a long-running render.
    const finishFinalize = (next: () => void) => {
      // eslint-disable-next-line no-console
      console.log('[finalizePlan] finishFinalize entered after', (Date.now() - startedAt) + 'ms');
      this.zone.run(() => {
        this.clarificationLoading = false;
        this.stopFinalizeStepper();
        try {
          next();
        } catch (renderErr) {
          // Defensive: any template / normalisation error shouldn't leave
          // the user stuck on the finalizing view.
          // eslint-disable-next-line no-console
          console.error('[finalizePlan] render error:', renderErr);
          this.clarificationError = 'Something went wrong showing the plan. Please try again.';
          this.toaster.error(this.clarificationError);
          this.state = 'clarification';
        }
        // Force a CD pass even if we arrived here outside the zone.
        try { this.cdr.detectChanges(); } catch (_) { /* view may be destroyed */ }
      });
    };

    this.aiService
      .finalizePlan(payload)
      .pipe(timeout(FINALIZE_PLAN_TIMEOUT_MS))
      .subscribe(
        (response: any) => {
          // eslint-disable-next-line no-console
          console.log('[finalizePlan] subscribe.next fired', {
            status: response && response.success,
            stage: response && (response.stage || (response.data && response.data.stage)),
            tangrams: response && response.data && Array.isArray(response.data.tangrams) ? response.data.tangrams.length : 0,
            sections: response && response.data && Array.isArray(response.data.sections) ? response.data.sections.length : 0,
          });
          finishFinalize(() => {
            if (!response || !response.success || !response.data) {
              const msg = (response && response.message) || 'Failed to finalise the plan. Please try again.';
              this.clarificationError = msg;
              this.toaster.error(msg);
              this.state = 'clarification';
              return;
            }
            const applied = this.applyPlanResponse(response.data);
            if (!applied) {
              this.clarificationError = 'No usable plan returned. Try rephrasing your answers.';
              this.toaster.warning(this.clarificationError);
              this.state = 'clarification';
            }
            // On success applyPlanResponse() has already transitioned to 'extracted'.
          });
        },
        (err: any) => {
          // eslint-disable-next-line no-console
          console.warn('[finalizePlan] subscribe.error fired', err && (err.name || err.status || err.message));
          finishFinalize(() => {
            // rxjs TimeoutError surfaces with name === 'TimeoutError'. Show a
            // message that steers the user toward retrying rather than making
            // them think the feature is broken.
            const isTimeout = err && (err.name === 'TimeoutError' || err instanceof Error && /timeout/i.test(err.message));
            const msg = isTimeout
              ? 'The AI took too long to respond. Please try again — simpler answers usually help.'
              : (err && err.error && err.error.message) || 'Failed to finalise the plan. Please try again.';
            this.clarificationError = msg;
            this.toaster.error(msg);
            this.state = 'clarification';
          });
        },
      );
  }

  /**
   * Manual escape hatch for the finalizing view. Shown once the slow hint
   * kicks in (30s+) — lets the user bail back to the clarification screen
   * without reloading the page when a proxy has eaten the response.
   */
  cancelFinalize() {
    this.clarificationLoading = false;
    this.stopFinalizeStepper();
    this.clarificationError = 'Finalising was cancelled. Adjust your answers and try again.';
    this.state = 'clarification';
    try { this.cdr.detectChanges(); } catch (_) { /* view may be destroyed */ }
  }

  /** Go back to the idle state without losing the uploaded file. */
  cancelClarification() {
    this.resetClarificationState();
    this.state = 'idle';
  }

  /**
   * Apply a `stage: "plan_ready"` response to component state. Returns false
   * when the response has no usable sections (caller surfaces an error).
   *
   * The legacy single-tangram shape is preserved on `this.extraction` for the
   * existing `extracted` / `decision` views. When the backend returned more
   * than one Tangram, we capture the AI's proposed split on
   * `aiSuggestedTangrams` so the grouping step can pre-fill with that split
   * instead of auto-splitting one item per group.
   */
  private applyPlanResponse(data: any): boolean {
    if (!data) { return false; }
    if (data.documentStructureId) {
      this.pendingDocumentStructureId = String(data.documentStructureId);
    }
    this.extraction = data;
    const rawSections = Array.isArray(data.sections) ? data.sections : [];

    // Each section is enriched with ALL MediaSetupItem fields inline so the
    // plan-review view can render previews, thumbnails, and YouTube trim
    // sliders on the same card the user is editing titles/descriptions on
    // — no separate "Set Up Media" step. `this.mediaSetupItems` is aliased
    // to the same array so the existing resolver + validator logic
    // (resolveMediaItem, validateMediaItem, hasSetupBlockers, etc.) keeps
    // working by index without any refactor.
    this.sections = rawSections.map((s: any, i: number) => {
      const url = String(s && s.videoURL || '').trim();
      const detected = this.detectPlatform(url);
      return {
        // Original section fields from the backend
        title: s && s.title ? String(s.title) : '',
        description: s && s.description ? String(s.description) : '',
        type: s && s.type ? String(s.type) : 'Document',
        order: s && s.order ? Number(s.order) : i + 1,
        tangramIndex: typeof (s && s.tangramIndex) === 'number' ? s.tangramIndex : 0,
        // MediaSetupItem fields used by the inline preview + trim UI
        id: this.newItemId(i),
        sectionIndex: i,
        aiTangramIndex: typeof (s && s.tangramIndex) === 'number' ? s.tangramIndex : -1,
        videoURL: url,
        platForm: url ? detected.platForm : 'None',
        youtubeVideoId: detected.youtubeVideoId,
        iFrameURL: null,
        previewURL: null,
        previewPlayerActive: false,
        thumbnail: { smallURL: '', mediumURL: '' },
        duration: { ...ZERO_TIME },
        durationSec: 0,
        startTime: { ...ZERO_TIME },
        endTime: { ...ZERO_TIME },
        startTimeInSecond: 0,
        endTimeInSecond: 0,
        tagId: this.deriveTagId(url, s && s.type),
        isResolving: false,
        isResolved: false,
        isYoutubeIdValid: detected.platForm === 'YouTube' && !!detected.youtubeVideoId,
        resolveError: '',
        startTimeError: '',
        endTimeError: '',
        placeholderPath: this.derivePlaceholderPath(url),
      };
    });
    this.mediaSetupItems = this.sections as any;

    const tangrams = Array.isArray(data.tangrams) ? data.tangrams : [];
    this.aiSuggestedTangrams = tangrams.length > 1
      ? tangrams.map((t: any, idx: number) => ({
        tangramIndex: typeof t.tangramIndex === 'number' ? t.tangramIndex : idx,
        tangramTitle: (t.tangramTitle || '').trim() || ('Tangram ' + (idx + 1)),
        tangramDescription: (t.tangramDescription || '').trim(),
        sectionIds: [],
      }))
      : [];

    if (!this.sections.length) { return false; }

    this.rebuildPlanGroups();
    this.resetClarificationState();
    this.state = 'extracted';

    // Kick off metadata resolution for every section that has a URL. These
    // run in parallel so the user can start editing while slower previews
    // load in. resolveMediaItem(index) operates on this.mediaSetupItems
    // which now points at this.sections, so the resolved state lands on
    // the same objects the plan-review template is rendering.
    this.sections.forEach((section, idx) => {
      if (section.videoURL) { this.resolveMediaItem(idx); }
    });
    return true;
  }

  // ── Reference-based helpers used by the inline plan-review UI ─────────
  // The plan-review template iterates `planGroups[i].sections` (a filtered
  // subset of `this.sections`), so the index inside the *ngFor doesn't map
  // to the global index the existing handlers expect. These wrappers take
  // the section by reference and look up the global index via indexOf.

  /** Find the canonical index for a section in `this.sections`. */
  private sectionIndex(section: any): number {
    return this.sections.indexOf(section);
  }

  onSectionUrlChange(section: any, value: string) {
    const idx = this.sectionIndex(section);
    if (idx !== -1) { this.onItemUrlChange(idx, value); }
  }

  onSectionSliderStart(section: any, event: Event) {
    const idx = this.sectionIndex(section);
    if (idx !== -1) { this.onSliderStart(idx, event); }
  }

  onSectionSliderEnd(section: any, event: Event) {
    const idx = this.sectionIndex(section);
    if (idx !== -1) { this.onSliderEnd(idx, event); }
  }

  onSectionTrimChange(section: any) {
    const idx = this.sectionIndex(section);
    if (idx !== -1) { this.onTrimChange(idx); }
  }

  /**
   * Full track gradient for dual-handle trim. Built in TS with colors read from
   * `getComputedStyle` so values are plain rgb/hex (Angular 8 does not reliably
   * apply `--trim-*` custom props via `[ngStyle]`, which was painting primary 0–100%).
   */
  getTrimTrackBackground(startSec: number, endSec: number, durationSec: number): string {
    if (typeof document === 'undefined' || !durationSec || durationSec <= 0) {
      return 'none';
    }
    const cs = getComputedStyle(document.documentElement);
    let primary = cs.getPropertyValue('--tangram-primary').trim();
    let off = cs.getPropertyValue('--tangram-slider-track-off').trim();
    if (!primary) { primary = '#7C3AED'; }
    if (!off) { off = '#cfd2e2'; }
    const p1 = Math.max(0, Math.min(100, (Number(startSec) || 0) / durationSec * 100));
    const p2 = Math.max(0, Math.min(100, (Number(endSec) || 0) / durationSec * 100));
    const lo = Math.min(p1, p2);
    const hi = Math.max(p1, p2);
    return 'linear-gradient(to right,' +
      ' ' + off + ' 0%,' +
      ' ' + off + ' ' + lo + '%,' +
      ' ' + primary + ' ' + lo + '%,' +
      ' ' + primary + ' ' + hi + '%,' +
      ' ' + off + ' ' + hi + '%,' +
      ' ' + off + ' 100%)';
  }

  // ── Decision step ───────────────────────────────────────────────
  /**
   * Transition from `extracted` → `decision`. Users explicitly pick whether
   * the extracted sections become one Tangram (Option A, existing behaviour)
   * or multiple Tangrams grouped from sections (Option B, new flow).
   */
  proceedToDecision() {
    if (!this.extraction || !this.sections.length) { return; }
    this.state = 'decision';
  }

  /** True when the AI returned more than one Tangram in its plan. */
  get aiRecommendsMulti(): boolean {
    return this.aiSuggestedTangrams.length > 1;
  }

  backToDecision() {
    if (!this.extraction) { return; }
    this.state = 'decision';
  }

  /** Option A — preserve legacy one-tangram-many-media behaviour. */
  chooseSingleMode() {
    this.creationMode = 'single';
    this.proceedToMediaSetup();
  }

  /** Option B — enter multi-tangram flow (media setup, then grouping). */
  chooseMultiMode() {
    this.creationMode = 'multi';
    this.groups = [];
    this.creationResults = [];
    this.isBatchCreating = false;
    this.proceedToMediaSetup();
  }

  // ── Media-setup step ────────────────────────────────────────────
  /**
   * Transition from `extracted` → `mediaSetup`. Builds one setup item per
   * section, triggers platform detection + metadata resolution for any item
   * that has a URL (mirroring `getPublicLinkDetails()` in create-tangram).
   *
   * Sections without a videoURL still participate in the setup step as
   * plain items (no trim, no preview) so the user can still edit/remove them
   * before the draft is created.
   */
  proceedToMediaSetup() {
    if (!this.extraction || !this.sections.length) { return; }
    // Default to single mode if the user skipped the decision step (keeps
    // backward compatibility with any direct call sites).
    if (!this.creationMode) { this.creationMode = 'single'; }
    this.mediaSetupError = '';
    this.mediaSetupItems = this.sections.map((s, i) => this.buildMediaSetupItem(s, i));
    this.state = 'mediaSetup';

    // Kick off resolution for any item with a URL. These run in parallel so
    // the user can start editing resolved items while the slower ones are
    // still loading.
    this.mediaSetupItems.forEach((item, idx) => {
      if (item.videoURL) { this.resolveMediaItem(idx); }
    });
  }

  backToExtracted() {
    // Keep extraction + sections intact so the user can tweak titles and
    // re-enter the setup step without losing context.
    this.state = 'extracted';
  }

  removeMediaItem(index: number) {
    this.mediaSetupItems.splice(index, 1);
    // Mirror the removal back into sections[] so if the user navigates back
    // to the extracted step the two views stay consistent.
    if (this.sections[index]) {
      this.sections.splice(index, 1);
    }
  }

  private buildMediaSetupItem(section: any, index: number): MediaSetupItem {
    const url = (section && section.videoURL ? String(section.videoURL) : '').trim();
    const detected = this.detectPlatform(url);
    return {
      id: this.newItemId(index),
      sectionIndex: index,
      aiTangramIndex: section && typeof section.tangramIndex === 'number' ? section.tangramIndex : -1,
      title: section && section.title ? String(section.title) : '',
      description: section && section.description ? String(section.description) : '',
      videoURL: url,
      type: section && section.type ? String(section.type) : 'Document',
      order: section && section.order ? Number(section.order) : index + 1,
      platForm: url ? detected.platForm : 'None',
      youtubeVideoId: detected.youtubeVideoId,
      iFrameURL: null,
      previewURL: null,
      previewPlayerActive: false,
      thumbnail: { smallURL: '', mediumURL: '' },
      duration: { ...ZERO_TIME },
      durationSec: 0,
      startTime: { ...ZERO_TIME },
      endTime: { ...ZERO_TIME },
      startTimeInSecond: 0,
      endTimeInSecond: 0,
      tagId: this.deriveTagId(url, section && section.type),
      isResolving: false,
      isResolved: false,
      isYoutubeIdValid: detected.platForm === 'YouTube' && !!detected.youtubeVideoId,
      resolveError: '',
      startTimeError: '',
      endTimeError: '',
      placeholderPath: this.derivePlaceholderPath(url),
    };
  }

  /**
   * Mirror of `getYoutubeId()` in create-tangram — pure function so it can be
   * called from both the initial map and from `onItemUrlChange()` when the
   * user edits a link inline.
   */
  private detectPlatform(rawUrl: string): { platForm: PlatForm; youtubeVideoId: string; isYoutubeIdValid: boolean } {
    if (!rawUrl) { return { platForm: 'None', youtubeVideoId: '', isYoutubeIdValid: false }; }
    const strVal = rawUrl.trim();
    try {
      if (strVal.includes('youtu.be')) {
        const url = new URL(strVal);
        const id = url.pathname.replace(/^\//, '').split(/[/?#]/)[0];
        return { platForm: 'YouTube', youtubeVideoId: id || '', isYoutubeIdValid: !!id };
      }
      if (strVal.includes('youtube.com')) {
        const url = new URL(strVal);
        const id = url.searchParams.get('v')
          || (url.pathname.match(/\/(embed|shorts)\/([^/?#]+)/) || [])[2]
          || '';
        return { platForm: 'YouTube', youtubeVideoId: id, isYoutubeIdValid: !!id };
      }
      if (strVal.includes('spotify')) {
        return { platForm: 'Spotify', youtubeVideoId: '', isYoutubeIdValid: false };
      }
      return { platForm: 'Other', youtubeVideoId: '', isYoutubeIdValid: false };
    } catch (_) {
      return { platForm: 'Other', youtubeVideoId: '', isYoutubeIdValid: false };
    }
  }

  /**
   * Mirror of the tag-assignment block in create-tangram's `onSubmit()`.
   * Keeps the mapping (YouTube → Video, spotify → Audio, .pdf → PDF, images →
   * Photo, docs/drive → Document) consistent with the manual flow so the
   * backend gets the same tagId signal whichever path created the media.
   */
  private deriveTagId(url: string, aiType: string): string {
    if (!url) {
      // Non-URL sections keep whatever the AI classified (Text/Document etc.)
      return aiType || 'Document';
    }
    const detected = this.detectPlatform(url);
    if (detected.platForm === 'YouTube') { return 'Video'; }
    if (/\bhttps?:\/\/\S+\.(?:png|jpe?g|gif|bmp|ico|svg)\b/i.test(url) || url.includes('image')) {
      return 'Photo';
    }
    if (url.includes('pdf')) { return 'PDF'; }
    if (url.includes('spotify')) { return 'Audio'; }
    if (url.includes('docs') || url.includes('drive')) { return 'Document'; }
    return aiType || 'Other';
  }

  private derivePlaceholderPath(url: string): string {
    if (!url) { return ''; }
    if (url.includes('pdf')) { return 'assets/img/videos/PDF_Placeholder.png'; }
    if (url.includes('docs') || url.includes('drive')) { return 'assets/img/videos/Drive_Placeholder.png'; }
    return 'assets/img/videos/Blog_Articles_Placeholder.png';
  }

  /**
   * Fetch platform-specific metadata for an item. Mirrors the branches in
   * `getPublicLinkDetails()` on create-tangram — YouTube goes through the
   * youtube API, Spotify through the backend proxy, everything else gets a
   * best-effort safe URL for preview.
   */
  resolveMediaItem(index: number) {
    const item = this.mediaSetupItems[index];
    if (!item || !item.videoURL) { return; }
    item.isResolving = true;
    item.isResolved = false;
    item.resolveError = '';

    if (item.platForm === 'YouTube' && item.youtubeVideoId) {
      this.createTangramService.getYoutubeVideoDetail(item.youtubeVideoId).subscribe(
        (data: any) => {
          if (data && Array.isArray(data.items) && data.items.length) {
            const [video] = data.items;
            const snippet = video.snippet || {};
            const contentDetails = video.contentDetails || {};
            const thumbnails = snippet.thumbnails || {};
            const duration = this.youtubeDurationToHms(contentDetails.duration || '');
            const durationSec = this.convertTimeToSecond(duration);
            const thumbnail = {
              smallURL: thumbnails.default ? thumbnails.default.url : '',
              mediumURL: thumbnails.medium ? thumbnails.medium.url : '',
            };
            // Preserve any edits the user already made to title/description —
            // only overwrite when the current value is empty.
            if (!item.title && snippet.title) { item.title = snippet.title; }
            if (!item.description && snippet.description) { item.description = snippet.description; }
            item.duration = duration;
            item.durationSec = durationSec;
            item.thumbnail = thumbnail;
            // Default trim window = full video, matching create-tangram's
            // initial `startTimeValue()` / `endTimeValue()` behaviour.
            item.startTime = { ...ZERO_TIME };
            item.endTime = { ...duration };
            item.startTimeInSecond = 0;
            item.endTimeInSecond = durationSec;
            // Don't build the iframe URL yet — we render a static thumbnail
            // first to avoid firing YouTube's logging/ad/lottie requests on
            // every resolved item. `activateYouTubePreview(item)` builds the
            // URL lazily the first time the user clicks the thumbnail.
            item.previewURL = null;
            item.previewPlayerActive = false;
            item.isResolved = true;
          } else {
            item.resolveError = 'Media does not exist';
          }
          item.isResolving = false;
        },
        () => {
          item.resolveError = 'Unable to fetch YouTube details. You can still edit the link manually.';
          item.isResolving = false;
        },
      );
      return;
    }

    if (item.platForm === 'Spotify') {
      this.createTangramService.getSpotifyUrlDetail(item.videoURL).subscribe(
        (response: any) => {
          const data = response && response.data;
          if (data) {
            if (!item.title && data.title) { item.title = data.title; }
            if (!item.description && data.description) { item.description = data.description; }
            const duration = this.msToHms(data.duration_ms || 0);
            item.duration = duration;
            item.durationSec = this.convertTimeToSecond(duration);
            if (data.thumbnail_url) {
              item.thumbnail = { smallURL: data.thumbnail_url, mediumURL: data.thumbnail_url };
            }
            if (data.iframe_url) {
              item.iFrameURL = this.sanitizer.bypassSecurityTrustResourceUrl(data.iframe_url);
            }
            item.isResolved = true;
          } else {
            item.resolveError = 'Media does not exist';
          }
          item.isResolving = false;
        },
        () => {
          item.resolveError = 'Unable to fetch Spotify details.';
          item.isResolving = false;
        },
      );
      return;
    }

    // Everything else — generic link / document / image. Produce a safe
    // preview URL using the same proxy rules as create-tangram, without
    // blocking the setup step on metadata we don't need here.
    this.resolveOtherLink(item);
  }

  private resolveOtherLink(item: MediaSetupItem) {
    const url = item.videoURL;
    if (/\bhttps?:\/\/\S+\.(?:png|jpe?g|gif|bmp|ico)\b/i.test(url)) {
      item.iFrameURL = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      if (!item.title) {
        const urlParts = url.split('/');
        const filenameWithQueryParams = urlParts[urlParts.length - 1] || '';
        const filenameWithoutQueryParams = filenameWithQueryParams.split('?')[0];
        const [imageName] = filenameWithoutQueryParams.split('.');
        if (imageName) { item.title = imageName; }
      }
    } else if (url.includes('document') || url.includes('spreadsheets') || url.includes('drive')) {
      const urlParts = url.split('/');
      urlParts[urlParts.length - 1] = 'preview';
      item.iFrameURL = this.sanitizer.bypassSecurityTrustResourceUrl(urlParts.join('/'));
    } else {
      const notAddProxy = ['wikipedia', 'axios', 'cbsnews', 'instructure.com', 'canvas-user-content.com', 'inst-fs-iad-prod.inscloudgate.net', '/api/v1/edlink/'];
      const notNeedProxy = notAddProxy.some((v) => url.includes(v));
      if (notNeedProxy) {
        item.iFrameURL = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      } else {
        const enCodeURL = encodeURIComponent(url);
        let newProxyURL = environment.proxyWorkerURL + '?id=1&interceptorNotNeeded=true&apiURL=' + enCodeURL;
        if (!environment.production) {
          newProxyURL = environment.proxyWorkerURL + '?id=2&interceptorNotNeeded=true&apiURL=' + enCodeURL;
        }
        item.iFrameURL = this.sanitizer.bypassSecurityTrustResourceUrl(newProxyURL);
      }
    }
    item.isResolved = true;
    item.isResolving = false;
  }

  /**
   * Re-run platform detection + metadata when the user edits a link inline.
   * Mirrors the `(input)` / paste handling on create-tangram where the form
   * resets and `getPublicLinkDetails()` is re-invoked.
   */
  onItemUrlChange(index: number, value: string) {
    const item = this.mediaSetupItems[index];
    if (!item) { return; }
    const trimmed = (value || '').trim();
    item.videoURL = trimmed;
    const detected = this.detectPlatform(trimmed);
    item.platForm = trimmed ? detected.platForm : 'None';
    item.youtubeVideoId = detected.youtubeVideoId;
    item.isYoutubeIdValid = detected.isYoutubeIdValid;
    item.tagId = this.deriveTagId(trimmed, item.type);
    item.placeholderPath = this.derivePlaceholderPath(trimmed);
    // Reset trim state — a different URL means a different duration.
    item.duration = { ...ZERO_TIME };
    item.durationSec = 0;
    item.startTime = { ...ZERO_TIME };
    item.endTime = { ...ZERO_TIME };
    item.startTimeInSecond = 0;
    item.endTimeInSecond = 0;
    item.startTimeError = '';
    item.endTimeError = '';
    item.iFrameURL = null;
    item.previewURL = null;
    item.previewPlayerActive = false;
    item.isResolved = false;
    item.resolveError = '';
    if (trimmed) { this.resolveMediaItem(index); }
  }

  /**
   * Build the YouTube embed URL for a video id. We use the
   * `youtube-nocookie.com` origin (privacy-enhanced mode) which skips the
   * tracking cookies and pagead/doubleclick beacons that the regular
   * `youtube.com/embed` fires on load. `autoplay=1` is appended because this
   * URL is only ever instantiated after the user clicks the thumbnail — see
   * `activateYouTubePreview()`.
   */
  private buildYouTubePreviewUrl(videoId: string, start: number = 0, end: number = 0): SafeResourceUrl {
    const params: string[] = ['rel=0', 'autoplay=1'];
    if (start > 0) { params.push('start=' + Math.floor(start)); }
    if (end > 0 && end > start) { params.push('end=' + Math.floor(end)); }
    const url = 'https://www.youtube-nocookie.com/embed/' + videoId + '?' + params.join('&');
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /**
   * Click-to-play gate for YouTube previews. Without this every resolved
   * YouTube item on the plan-review / media-setup screens mounts a full
   * iframe on load — each one firing ~20-30 log/pagead/lottie requests (so
   * an 8-item plan would dispatch ~200 requests before the user has touched
   * anything). We defer iframe creation until the user actually wants to
   * watch by flipping `previewPlayerActive`; the template renders a static
   * thumbnail until then.
   */
  activateYouTubePreview(item: any) {
    if (!item || item.platForm !== 'YouTube' || !item.youtubeVideoId) { return; }
    // Only (re)build the URL on activation, not on every resolve, so the
    // user sees the current trim window when they press play after adjusting
    // the start/end sliders.
    item.previewURL = this.buildYouTubePreviewUrl(
      item.youtubeVideoId,
      item.startTimeInSecond || 0,
      item.endTimeInSecond || 0,
    );
    item.previewPlayerActive = true;
  }

  /**
   * Static thumbnail URL for a YouTube video id — used by the click-to-play
   * preview card so we never touch `youtube.com` on page load. mqdefault is
   * available for every valid video id (hqdefault occasionally 404s on
   * Shorts / some auto-generated uploads).
   */
  youtubeThumbUrl(item: any): string {
    if (!item || !item.youtubeVideoId) {
      return (item && item.thumbnail && item.thumbnail.mediumURL) || '';
    }
    return 'https://i.ytimg.com/vi/' + item.youtubeVideoId + '/mqdefault.jpg';
  }

  /** Template convenience used by the click-to-play preview. */
  onYouTubePreviewClick(item: any) {
    this.activateYouTubePreview(item);
  }

  private secondsToHms(seconds: number): TimeObj {
    const s = Math.max(0, Math.floor(seconds || 0));
    return { hour: Math.floor(s / 3600), minute: Math.floor((s % 3600) / 60), second: s % 60 };
  }

  formatHms(seconds: number): string {
    const s = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return pad(h) + ':' + pad(m) + ':' + pad(sec);
  }

  onSliderStart(index: number, event: Event) {
    const item = this.mediaSetupItems[index];
    if (!item) { return; }
    let val = parseInt((event.target as HTMLInputElement).value, 10) || 0;
    // Keep start strictly below end so the range stays valid and the track
    // fill is always positive, mirroring `handleDuration()`'s ordering rule.
    if (val >= item.endTimeInSecond) {
      val = Math.max(0, item.endTimeInSecond - 1);
      (event.target as HTMLInputElement).value = String(val);
    }
    item.startTimeInSecond = val;
    item.startTime = this.secondsToHms(val);
    this.validateMediaItem(item);
  }

  onSliderEnd(index: number, event: Event) {
    const item = this.mediaSetupItems[index];
    if (!item) { return; }
    let val = parseInt((event.target as HTMLInputElement).value, 10) || 0;
    if (val <= item.startTimeInSecond) {
      val = Math.min(item.durationSec, item.startTimeInSecond + 1);
      (event.target as HTMLInputElement).value = String(val);
    }
    if (val > item.durationSec) { val = item.durationSec; }
    item.endTimeInSecond = val;
    item.endTime = this.secondsToHms(val);
    this.validateMediaItem(item);
  }

  /**
   * Validate start/end trim for a single item. Mirrors the rules in
   * `onSubmit()` / `handleDuration()`:
   *   start < duration
   *   end   <= duration
   *   start <= end
   * YouTube is the only platform that carries a real duration, so trim
   * validation is skipped for everything else (matches create-tangram, which
   * only checks `platForm == 'YouTube'`).
   */
  private validateMediaItem(item: MediaSetupItem): boolean {
    item.startTimeError = '';
    item.endTimeError = '';
    if (item.platForm !== 'YouTube' || !item.isResolved) { return true; }

    // Fallback defaults if the user cleared one of the timepickers.
    if (!item.startTime) { item.startTime = { ...ZERO_TIME }; }
    if (!item.endTime) { item.endTime = { ...item.duration }; }

    const startSec = this.convertTimeToSecond(item.startTime);
    const endSec = this.convertTimeToSecond(item.endTime);
    item.startTimeInSecond = startSec;
    item.endTimeInSecond = endSec;

    let ok = true;
    if (startSec >= item.durationSec) {
      item.startTimeError = 'Start time should not be bigger that video duration.';
      ok = false;
    }
    if (endSec > item.durationSec) {
      item.endTimeError = 'End time should not be bigger than video duration.';
      ok = false;
    }
    if (ok && startSec > endSec) {
      item.startTimeError = 'Start time should not be bigger than End time';
      ok = false;
    }
    return ok;
  }

  onTrimChange(index: number) {
    const item = this.mediaSetupItems[index];
    if (!item) { return; }
    this.validateMediaItem(item);
  }

  /** Convenience used by the template to enable/disable the confirm button. */
  get hasSetupBlockers(): boolean {
    if (!this.mediaSetupItems.length) { return true; }
    return this.mediaSetupItems.some((i) => i.isResolving);
  }

  // ── Time helpers (local copies so we don't reach into CreateTangramComponent) ──
  private convertTimeToSecond(time: TimeObj): number {
    if (!time) { return 0; }
    return (Number(time.hour) || 0) * 3600 + (Number(time.minute) || 0) * 60 + (Number(time.second) || 0);
  }

  private youtubeDurationToHms(duration: string): TimeObj {
    const videoDuration: TimeObj = { hour: 0, minute: 0, second: 0 };
    if (!duration) { return videoDuration; }
    duration = duration.replace('PT', '');
    const hourIndex = duration.indexOf('H');
    if (hourIndex !== -1) {
      videoDuration.hour = parseInt(duration.substring(0, hourIndex), 10) || 0;
    }
    const minuteIndex = duration.indexOf('M');
    if (minuteIndex !== -1) {
      videoDuration.minute = parseInt(duration.substring(hourIndex + 1, minuteIndex), 10) || 0;
    }
    const secondIndex = duration.indexOf('S');
    if (secondIndex !== -1) {
      videoDuration.second = parseInt(duration.substring(minuteIndex + 1, secondIndex), 10) || 0;
    }
    return videoDuration;
  }

  private msToHms(ms: number): TimeObj {
    const seconds = Math.floor((ms || 0) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return { hour: hours, minute: minutes % 60, second: seconds % 60 };
  }

  // ── Draft creation ──────────────────────────────────────────────
  /**
   * Run per-item validation — shared between single-mode confirm and
   * multi-mode "proceed to grouping" so both flows enforce the exact same
   * rules (non-empty title, YouTube trim bounds, no in-flight resolutions).
   */
  private validateAllMediaItems(): { ok: boolean; message: string } {
    if (!this.mediaSetupItems.length) {
      return { ok: false, message: 'At least one item is required to create the draft.' };
    }
    let firstError = '';
    let ok = true;
    for (const item of this.mediaSetupItems) {
      if (item.isResolving) {
        ok = false;
        firstError = firstError || 'Please wait — some links are still being checked.';
        continue;
      }
      if (!item.title || !item.title.trim()) {
        ok = false;
        firstError = firstError || 'Each item needs a title.';
      }
      if (!this.validateMediaItem(item)) {
        ok = false;
        firstError = firstError || (item.startTimeError || item.endTimeError);
      }
    }
    return { ok, message: ok ? '' : (firstError || 'Please fix the highlighted items before continuing.') };
  }

  confirmMediaSetup() {
    const validation = this.validateAllMediaItems();
    if (!validation.ok) {
      this.mediaSetupError = validation.message;
      this.toaster.error(this.mediaSetupError);
      return;
    }
    this.mediaSetupError = '';

    // Single-mode — preserve existing one-tangram-many-media behaviour.
    if (this.creationMode !== 'multi') {
      this.createDraft();
      return;
    }

    // Multi-mode — transition to grouping with one default group that holds
    // every item (user can split from there).
    this.enterGrouping();
  }

  /**
   * One-click creation from the plan-review screen. Replaces the old
   * decision → mediaSetup → grouping chain:
   *   • Validates every section (title non-empty, YouTube trim bounds valid,
   *     no in-flight resolutions).
   *   • If the AI returned a single Tangram → creates one draft with all
   *     sections (same as the legacy single-mode `createDraft()`).
   *   • If the AI returned multiple Tangrams → builds groups from each
   *     section's `tangramIndex`, then fires the existing batch creator
   *     (`createTangramsBatch`). The user never sees a grouping UI — the
   *     AI's split is honoured directly.
   *
   * Called from the "Create N Tangrams" button on the extracted view.
   */
  confirmCreatePlan() {
    const validation = this.validateAllMediaItems();
    if (!validation.ok) {
      this.mediaSetupError = validation.message;
      this.toaster.error(this.mediaSetupError);
      return;
    }
    this.mediaSetupError = '';

    const multi = this.aiSuggestedTangrams.length > 1;
    if (!multi) {
      this.creationMode = 'single';
      this.createDraft();
      return;
    }

    // Multi-Tangram: build groups directly from the AI's split. No manual
    // grouping UI — every section goes into its `aiTangramIndex` group.
    this.creationMode = 'multi';
    this.groups = this.buildGroupsFromAiSuggestion();
    if (!this.groups.length) {
      // Defensive — shouldn't happen, but fall back to a single default.
      this.groups = this.buildGroupsOnePerItem();
    }
    this.isBatchCreating = true;
    this.creationResults = this.groups.map((g) => ({
      groupId: g.id,
      groupTitle: (g.title || '').trim() || 'Untitled',
      status: 'pending' as 'pending',
    }));
    this.state = 'creating';
    this.createTangramsBatch(0);
  }

  /** Convenience: how many Tangrams the "Create" button will produce. */
  get planTangramCount(): number {
    const current = this.extraction && Array.isArray(this.extraction.tangrams)
      ? this.extraction.tangrams.length
      : 0;
    return Math.max(1, current || this.aiSuggestedTangrams.length);
  }

  /** Number of Tangrams that have at least one media item. Empty Tangrams
   * are excluded from header count and creation — they can't be created. */
  get planTangramWithMediaCount(): number {
    return this.planGroups.filter((g) => g.mediaCount > 0).length;
  }

  // ── Plan-editing actions (live on the extracted/plan-review screen) ────

  /**
   * Move a media section into a different Tangram by its current position
   * in `extraction.tangrams`. Called from the "Move to…" dropdown on each
   * plan-media-item card.
   */
  moveSectionToTangramIndex(section: any, targetIndex: number) {
    const tangrams = this.extraction && Array.isArray(this.extraction.tangrams) ? this.extraction.tangrams : [];
    if (!section || targetIndex < 0 || targetIndex >= tangrams.length) { return; }
    if (section.tangramIndex === targetIndex) { return; }
    section.tangramIndex = targetIndex;
    this.rebuildPlanGroups();
  }

  /** Template convenience — dropdown binds to a number, coerce safely. */
  moveSectionToTangramEvent(section: any, value: any) {
    const idx = Number(value);
    if (Number.isFinite(idx)) { this.moveSectionToTangramIndex(section, idx); }
  }

  /** Swap Tangram at position `i` with the one above it, keeping each
   * section pointing at the same logical Tangram. */
  moveTangramUp(i: number) {
    const tangrams = this.extraction && Array.isArray(this.extraction.tangrams) ? this.extraction.tangrams : null;
    if (!tangrams || i <= 0 || i >= tangrams.length) { return; }
    const tmp = tangrams[i];
    tangrams[i] = tangrams[i - 1];
    tangrams[i - 1] = tmp;
    for (const s of this.sections) {
      if (s.tangramIndex === i) { s.tangramIndex = i - 1; }
      else if (s.tangramIndex === i - 1) { s.tangramIndex = i; }
    }
    this.rebuildPlanGroups();
  }

  moveTangramDown(i: number) {
    const tangrams = this.extraction && Array.isArray(this.extraction.tangrams) ? this.extraction.tangrams : null;
    if (!tangrams || i < 0 || i >= tangrams.length - 1) { return; }
    const tmp = tangrams[i];
    tangrams[i] = tangrams[i + 1];
    tangrams[i + 1] = tmp;
    for (const s of this.sections) {
      if (s.tangramIndex === i) { s.tangramIndex = i + 1; }
      else if (s.tangramIndex === i + 1) { s.tangramIndex = i; }
    }
    this.rebuildPlanGroups();
  }

  /** Restore the AI's originally proposed split. */
  resetPlanToAiSplit() {
    if (!this.aiSuggestedTangrams.length || !this.extraction) { return; }

    // Rebuild the tangrams array from the captured AI suggestion. We carry
    // tangramIndex (its current position) + basic title/description; no
    // sections are stored here because the plan-review groups sections via
    // `section.tangramIndex` lookup.
    this.extraction.tangrams = this.aiSuggestedTangrams.map((ai, i) => ({
      tangramIndex: i,
      tangramTitle: ai.tangramTitle,
      tangramDescription: ai.tangramDescription,
      sections: [],
      extractedLinks: [],
    }));

    // Restore each section's Tangram assignment to the AI's original pick.
    for (const s of this.sections) {
      s.tangramIndex = typeof s.aiTangramIndex === 'number' && s.aiTangramIndex >= 0 ? s.aiTangramIndex : 0;
    }
    this.rebuildPlanGroups();
  }

  /** One Tangram per media item (mirror of the old grouping "Auto-split"). */
  splitPlanOnePerItem() {
    if (!this.sections.length || !this.extraction) { return; }

    this.extraction.tangrams = this.sections.map((s, i) => ({
      tangramIndex: i,
      tangramTitle: (s.title && s.title.trim()) || ('Tangram ' + (i + 1)),
      tangramDescription: (s.description && s.description.trim()) || '',
      sections: [],
      extractedLinks: [],
    }));
    this.sections.forEach((s, i) => { s.tangramIndex = i; });
    this.rebuildPlanGroups();
  }

  /** Merge all media items into a single Tangram (mirror of "Merge all"). */
  mergePlanIntoOneTangram() {
    if (!this.sections.length || !this.extraction) { return; }

    const firstExisting = Array.isArray(this.extraction.tangrams) && this.extraction.tangrams[0];
    this.extraction.tangrams = [{
      tangramIndex: 0,
      tangramTitle: (firstExisting && firstExisting.tangramTitle)
        || (this.extraction.tangramTitle && this.extraction.tangramTitle.trim())
        || 'Tangram 1',
      tangramDescription: (firstExisting && firstExisting.tangramDescription)
        || (this.extraction.tangramDescription && this.extraction.tangramDescription.trim())
        || '',
      sections: [],
      extractedLinks: [],
    }];
    for (const s of this.sections) { s.tangramIndex = 0; }
    this.rebuildPlanGroups();
  }

  addPlanTangram() {
    if (!this.extraction) { return; }
    if (!Array.isArray(this.extraction.tangrams)) {
      this.extraction.tangrams = [];
    }
    const newIndex = this.extraction.tangrams.length;
    this.extraction.tangrams.push({
      tangramIndex: newIndex,
      tangramTitle: 'Tangram ' + (newIndex + 1),
      tangramDescription: '',
      overallSummary: '',
      extractedLinks: [],
    });
    this.rebuildPlanGroups();
  }

  addMediaToPlanTangram(tangramIndex: number, url: string) {
    const raw = (url || '').trim();
    if (!raw) { return; }
    const detected = this.detectPlatform(raw);
    const newSection: any = {
      title: '',
      description: '',
      type: detected.platForm === 'YouTube' ? 'Video' : 'Document',
      order: this.sections.length + 1,
      tangramIndex,
      id: this.newItemId(this.sections.length),
      sectionIndex: this.sections.length,
      aiTangramIndex: tangramIndex,
      videoURL: raw,
      platForm: detected.platForm,
      youtubeVideoId: detected.youtubeVideoId,
      iFrameURL: null,
      previewURL: null,
      previewPlayerActive: false,
      thumbnail: { smallURL: '', mediumURL: '' },
      duration: { ...ZERO_TIME },
      durationSec: 0,
      startTime: { ...ZERO_TIME },
      endTime: { ...ZERO_TIME },
      startTimeInSecond: 0,
      endTimeInSecond: 0,
      tagId: this.deriveTagId(raw, null),
      isResolving: false,
      isResolved: false,
      isYoutubeIdValid: detected.platForm === 'YouTube' && !!detected.youtubeVideoId,
      resolveError: '',
      startTimeError: '',
      endTimeError: '',
      placeholderPath: this.derivePlaceholderPath(raw),
    };
    this.sections.push(newSection);
    this.mediaSetupItems = this.sections as any;
    this.rebuildPlanGroups();
    // Kick off metadata resolution (YouTube title/duration, Spotify embed)
    this.resolveMediaItem(this.sections.length - 1);
  }

  /** Track the underlying extraction.tangrams entry for a plan group. Used
   * by the Tangram-title input so edits persist past rebuildPlanGroups. */
  currentTangramRef(gi: number): any {
    const tangrams = this.extraction && Array.isArray(this.extraction.tangrams) ? this.extraction.tangrams : [];
    return tangrams[gi] || null;
  }

  /** Disable the Create CTA while any section is still resolving metadata
   * or has a fatal validation error the user must fix first. */
  get hasPlanBlockers(): boolean {
    if (!this.sections.length) { return true; }
    if (this.planTangramWithMediaCount === 0) { return true; }
    for (const s of this.sections) {
      if (s.isResolving) { return true; }
    }
    return false;
  }

  // ── Grouping step (multi-mode) ──────────────────────────────────
  /**
   * Seed the grouping UI. Preference order when the user first enters this
   * step:
   *   1. AI's proposed split — if the plan came back with multiple Tangrams,
   *      use that grouping (the whole point of the analysis pipeline).
   *   2. One Tangram per item — the legacy auto-split fallback.
   *
   * Re-entry from `backToMediaSetup()` keeps any custom grouping the user
   * already built (we only seed defaults when `groups` is empty).
   */
  enterGrouping() {
    if (!this.mediaSetupItems.length) {
      this.toaster.error('No items to group.');
      return;
    }
    if (!this.groups.length) {
      this.groups = this.aiSuggestedTangrams.length > 1
        ? this.buildGroupsFromAiSuggestion()
        : this.buildGroupsOnePerItem();
    }
    this.groupingError = '';
    this.state = 'grouping';
  }

  /**
   * Turn `aiSuggestedTangrams` + each item's `aiTangramIndex` into concrete
   * TangramGroup records. Items with no assignment (aiTangramIndex === -1)
   * land in the first group so nothing is lost.
   */
  private buildGroupsFromAiSuggestion(): TangramGroup[] {
    const groups: TangramGroup[] = this.aiSuggestedTangrams.map((t) => this.makeGroup(
      t.tangramTitle || ('Tangram ' + (t.tangramIndex + 1)),
      t.tangramDescription || '',
      [],
    ));
    // Bucket items by their AI-proposed tangramIndex.
    for (const item of this.mediaSetupItems) {
      const groupIdx = (item.aiTangramIndex >= 0 && item.aiTangramIndex < groups.length) ? item.aiTangramIndex : 0;
      groups[groupIdx].itemIds.push(item.id);
    }
    // Drop any groups that ended up empty (defensive — shouldn't happen when
    // the AI split is well-formed).
    return groups.filter((g) => g.itemIds.length > 0);
  }

  private buildGroupsOnePerItem(): TangramGroup[] {
    return this.mediaSetupItems.map((item, idx) => this.makeGroup(
      (item.title && item.title.trim()) || ('Tangram ' + (idx + 1)),
      (item.description && item.description.trim()) || '',
      [item.id],
    ));
  }

  /**
   * Convenience action surfaced in the grouping UI — merges all items into a
   * single group, undoing the auto-split. Useful when the user actually wants
   * one Tangram with many media items but is already in multi mode.
   */
  mergeAllIntoOneGroup() {
    if (!this.mediaSetupItems.length) { return; }
    const allIds = this.mediaSetupItems.map((i) => i.id);
    this.groups = [this.makeGroup(
      (this.extraction && this.extraction.tangramTitle) || 'Tangram 1',
      (this.extraction && this.extraction.tangramDescription) || '',
      allIds,
    )];
  }

  /** Restore the default one-Tangram-per-item layout from the grouping UI. */
  resetGroupsToAutoSplit() {
    if (!this.mediaSetupItems.length) { return; }
    this.groups = this.buildGroupsOnePerItem();
  }

  /** Restore the AI's originally-suggested grouping. No-op if the AI only
   * produced a single Tangram. */
  resetGroupsToAiSuggestion() {
    if (!this.mediaSetupItems.length || !this.aiRecommendsMulti) { return; }
    this.groups = this.buildGroupsFromAiSuggestion();
  }

  backToMediaSetup() {
    this.state = 'mediaSetup';
  }

  addGroup() {
    const title = 'Tangram ' + (this.groups.length + 1);
    this.groups.push(this.makeGroup(title, '', []));
  }

  removeGroup(groupId: string) {
    if (this.groups.length <= 1) {
      this.toaster.warning('At least one group is required.');
      return;
    }
    const group = this.groups.find((g) => g.id === groupId);
    if (!group) { return; }
    // Move any items in this group to the first remaining group so no item
    // is lost when a group is removed.
    const remaining = this.groups.filter((g) => g.id !== groupId);
    if (group.itemIds.length && remaining[0]) {
      for (const id of group.itemIds) {
        if (!remaining[0].itemIds.includes(id)) { remaining[0].itemIds.push(id); }
      }
    }
    this.groups = remaining;
  }

  /**
   * Move an item to a different group. Removes the item from every other
   * group first so there's never a duplicate reference.
   */
  moveItemToGroup(itemId: string, toGroupId: string) {
    if (!itemId || !toGroupId) { return; }
    for (const g of this.groups) {
      g.itemIds = g.itemIds.filter((id) => id !== itemId);
    }
    const target = this.groups.find((g) => g.id === toGroupId);
    if (target && !target.itemIds.includes(itemId)) {
      target.itemIds.push(itemId);
    }
  }

  /** Helper for the template: which group currently holds this item. */
  getGroupIdForItem(itemId: string): string {
    const g = this.groups.find((grp) => grp.itemIds.indexOf(itemId) !== -1);
    return g ? g.id : '';
  }

  getGroupItems(group: TangramGroup): MediaSetupItem[] {
    const byId = new Map<string, MediaSetupItem>();
    for (const i of this.mediaSetupItems) { byId.set(i.id, i); }
    const out: MediaSetupItem[] = [];
    for (const id of group.itemIds) {
      const item = byId.get(id);
      if (item) { out.push(item); }
    }
    return out;
  }

  private validateGroups(): { ok: boolean; message: string } {
    if (!this.groups.length) { return { ok: false, message: 'At least one Tangram group is required.' }; }
    for (const g of this.groups) {
      if (!g.title || !g.title.trim()) {
        return { ok: false, message: 'Each Tangram needs a title.' };
      }
      if (!g.itemIds.length) {
        return { ok: false, message: '"' + g.title + '" has no media items. Move at least one item into it or remove the group.' };
      }
    }
    // Sanity: every media item must belong to exactly one group (the UI
    // enforces this, but guard against corrupted state from edge cases).
    const assigned = new Set<string>();
    for (const g of this.groups) {
      for (const id of g.itemIds) { assigned.add(id); }
    }
    for (const item of this.mediaSetupItems) {
      if (!assigned.has(item.id)) {
        return { ok: false, message: 'Some media items are not assigned to any Tangram.' };
      }
    }
    return { ok: true, message: '' };
  }

  confirmGrouping() {
    // Guard against double-click while a previous batch is still in flight.
    if (this.isBatchCreating) { return; }

    // Re-run per-item validation in case titles were edited in grouping.
    const itemValidation = this.validateAllMediaItems();
    if (!itemValidation.ok) {
      this.groupingError = itemValidation.message;
      this.toaster.error(this.groupingError);
      return;
    }
    const groupValidation = this.validateGroups();
    if (!groupValidation.ok) {
      this.groupingError = groupValidation.message;
      this.toaster.error(this.groupingError);
      return;
    }

    this.groupingError = '';
    this.isBatchCreating = true;
    // Build a CreationResult per group up front so the UI can render the
    // per-group progress list as soon as the `creating` state is shown.
    this.creationResults = this.groups.map((g) => ({
      groupId: g.id,
      groupTitle: (g.title || '').trim() || 'Untitled',
      status: 'pending' as 'pending',
    }));
    this.state = 'creating';
    this.createTangramsBatch(0);
  }

  /**
   * Sequential creation of one draft tangram per group. Sequential (not
   * parallel) so we preserve ordering, stay within backend rate limits, and
   * surface per-group failures independently. Any individual failure does
   * NOT abort the remaining creations — we record it and continue.
   *
   * The recursive-subscribe chain is guarded with explicit index and length
   * checks, and the in-flight flag (`isBatchCreating`) is only cleared from
   * the terminal branch so partial mutations can't happen mid-flight.
   */
  private createTangramsBatch(index: number) {
    // Terminal branch — all groups attempted. Transition to `done`, flip the
    // flag, and surface a summary toast so the user knows the overall result.
    if (index >= this.groups.length) {
      this.isBatchCreating = false;
      this.state = 'done';
      const total = this.creationResults.length;
      const successful = this.creationResults.filter((r) => r.status === 'success');
      const failed = this.creationResults.filter((r) => r.status === 'failed');
      if (successful.length && !failed.length) {
        this.toaster.success('Created ' + successful.length + ' draft Tangram' + (successful.length === 1 ? '' : 's') + '.');
        // Emit for the last successful pathway so the parent can navigate if
        // it wants (we emit one event per multi-batch to stay compatible
        // with the existing @Output contract).
        const last = successful[successful.length - 1];
        if (last && last.pathwayId) {
          this.draftCreated.emit({
            pathwayId: last.pathwayId,
            tangramTitle: last.groupTitle,
            mediaCount: last.mediaCount || 0,
          });
        }
      } else if (successful.length && failed.length) {
        this.toaster.warning('Created ' + successful.length + ' of ' + total + ' Tangrams. ' + failed.length + ' failed — retry from the list below.');
      } else {
        this.toaster.error('No Tangrams were created. See the error details below.');
      }
      return;
    }

    const group = this.groups[index];
    const result = this.creationResults[index];

    // Defensive: if either array is somehow shorter than expected, skip and
    // move on so we don't crash mid-batch. (Should never happen because
    // `confirmGrouping()` builds results with `this.groups.map(...)`.)
    if (!group || !result) {
      this.createTangramsBatch(index + 1);
      return;
    }

    result.status = 'creating';

    const payload = {
      tangramTitle: (group.title || '').trim(),
      tangramDescription: (group.description || '').trim(),
      sections: this.buildGroupSectionsPayload(group),
    };

    // Safety: empty sections would 400 on the backend. Fail this group fast
    // and continue so a mis-grouped payload can't stall the batch.
    if (!payload.sections.length) {
      result.status = 'failed';
      result.error = 'This Tangram had no valid media items.';
      this.createTangramsBatch(index + 1);
      return;
    }

    this.aiService.createDraftFromDocument(payload).subscribe(
      (response: any) => {
        if (response && response.success && response.data) {
          result.status = 'success';
          result.pathwayId = response.data.pathwayId;
          result.mediaCount = response.data.mediaCount;
        } else {
          result.status = 'failed';
          result.error = (response && response.message) || 'Creation failed.';
        }
        this.createTangramsBatch(index + 1);
      },
      (err: any) => {
        result.status = 'failed';
        result.error = (err && err.error && err.error.message) || (err && err.message) || 'Creation failed.';
        this.createTangramsBatch(index + 1);
      },
    );
  }

  private buildGroupSectionsPayload(group: TangramGroup) {
    const items = this.getGroupItems(group);
    return items.map((item, i) => ({
      title: (item.title || '').trim(),
      description: (item.description || '').trim(),
      videoURL: (item.videoURL || '').trim(),
      type: item.type || 'Document',
      order: i + 1,
      platForm: item.platForm === 'None' ? '' : item.platForm,
      youtubeVideoId: item.youtubeVideoId,
      duration: item.duration,
      startTime: item.startTime,
      endTime: item.endTime,
      startTimeInSecond: item.startTimeInSecond,
      endTimeInSecond: item.endTimeInSecond,
      thumbnail: item.thumbnail,
      tagId: item.tagId,
    }));
  }

  /** Count of successfully created tangrams — used by the multi-mode summary header. */
  creationResultsSuccessCount(): number {
    return this.creationResults.filter((r) => r.status === 'success').length;
  }

  /** Open a successfully-created draft from the multi-mode success screen. */
  viewGroupDraft(result: CreationResult) {
    if (result && result.status === 'success' && result.pathwayId) {
      // Newly created Tangrams are drafts — open the draft edit view so the
      // user can review, add links, and publish (not the read-only dashboard).
      this.router.navigate(['/edit-tangram', result.pathwayId]);
    }
  }

  /** Retry a single failed group creation from the success summary. */
  retryGroupCreation(result: CreationResult) {
    if (!result || result.status !== 'failed') { return; }
    const group = this.groups.find((g) => g.id === result.groupId);
    if (!group) { return; }
    result.status = 'creating';
    result.error = '';
    const payload = {
      tangramTitle: (group.title || '').trim(),
      tangramDescription: (group.description || '').trim(),
      sections: this.buildGroupSectionsPayload(group),
    };
    this.aiService.createDraftFromDocument(payload).subscribe(
      (response: any) => {
        if (response && response.success && response.data) {
          result.status = 'success';
          result.pathwayId = response.data.pathwayId;
          result.mediaCount = response.data.mediaCount;
          this.toaster.success('"' + result.groupTitle + '" created.');
        } else {
          result.status = 'failed';
          result.error = (response && response.message) || 'Creation failed.';
        }
      },
      (err: any) => {
        result.status = 'failed';
        result.error = (err && err.error && err.error.message) || 'Creation failed.';
      },
    );
  }

  private makeGroup(title: string, description: string, itemIds: string[]): TangramGroup {
    this.groupSeq++;
    return {
      id: 'g-' + this.groupSeq + '-' + Date.now(),
      title,
      description,
      itemIds: [...itemIds],
    };
  }

  private newItemId(index: number): string {
    return 'i-' + index + '-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
  }

  private buildSectionsPayload() {
    return this.mediaSetupItems.map((item, i) => ({
      title: (item.title || '').trim(),
      description: (item.description || '').trim(),
      videoURL: (item.videoURL || '').trim(),
      type: item.type || 'Document',
      order: item.order || i + 1,
      // Extra fields that the manual create flow sends. Current backend
      // ignores unknown properties on `sections[]` items — keeping them on
      // the payload means once the backend is updated to persist trim data
      // per section, no further client change is needed.
      platForm: item.platForm === 'None' ? '' : item.platForm,
      youtubeVideoId: item.youtubeVideoId,
      duration: item.duration,
      startTime: item.startTime,
      endTime: item.endTime,
      startTimeInSecond: item.startTimeInSecond,
      endTimeInSecond: item.endTimeInSecond,
      thumbnail: item.thumbnail,
      tagId: item.tagId,
    }));
  }

  createDraft() {
    if (!this.extraction) { return; }
    this.state = 'creating';
    this.errorMessage = '';
    const payload = {
      tangramTitle: this.extraction.tangramTitle,
      tangramDescription: this.extraction.tangramDescription,
      sections: this.buildSectionsPayload(),
    };
    this.aiService.createDraftFromDocument(payload).subscribe(
      (response: any) => {
        if (response && response.success && response.data) {
          this.createdPathwayId = response.data.pathwayId;
          this.state = 'done';
          this.toaster.success('Draft tangram created with ' + response.data.mediaCount + ' media items.');
          this.draftCreated.emit({
            pathwayId: response.data.pathwayId,
            tangramTitle: response.data.tangramTitle,
            mediaCount: response.data.mediaCount,
          });
        } else {
          const msg = (response && response.message) || 'Failed to create draft.';
          this.errorMessage = msg;
          this.toaster.error(msg);
          this.state = 'mediaSetup';
        }
      },
      (err: any) => {
        const msg = (err && err.error && err.error.message) || 'Failed to create draft tangram.';
        this.errorMessage = msg;
        this.toaster.error(msg);
        this.state = 'mediaSetup';
      },
    );
  }

  viewDraft() {
    if (this.createdPathwayId) {
      // Open the draft edit view (not the read-only dashboard) so the user
      // can review/edit/add links and publish the freshly created draft.
      this.router.navigate(['/edit-tangram', this.createdPathwayId]);
    }
  }

  /** Multi-mode: open the first Tangram we just created (matches the per-row Open). */
  goToCreatedTangram() {
    const first = this.creationResults.find((r) => r.status === 'success' && r.pathwayId);
    if (first) {
      this.router.navigate(['/edit-tangram', first.pathwayId]);
    }
  }

  resetFlow() {
    this.clearFile();
  }
}
